import {APIService} from '@custom-types/APIService';
import {APIServiceResponse} from '@custom-types/APIServiceResponse';
import {IconMap} from '@new-components/templates/SearchBox/useSearchState';
import {HttpRequest} from '@services/HttpClient';
import {trackErrorEvents} from '@utils/googleAnalytics/googleAnalyticsEvents';
import {getAPIUrl} from '@utils/service.utils';

type ResponseDataGFR = {
  amenities: Array<string>;
  areas: Array<number>;
  bathrooms: Array<number>;
  bedrooms: Array<number>;
  completionStatus: Array<string>;
  prices: Array<number>;
  types: Array<string>;
  unitView: Array<string>;
  resaleTypes: Array<string>;
};

type GFRResponse = APIServiceResponse<ResponseDataGFR>;

type PropertyType = {
  iconType: keyof typeof IconMap;
  label: string;
  value: string;
};
export type ParsedDataGFR = {
  [key in keyof Omit<ResponseDataGFR, 'prices' | 'areas' | 'types'>]: Array<{
    value: string;
    label: string;
  }>;
} & {
  price: Record<'min' | 'max', number>;
  area: Record<'min' | 'max', number>;
  types: PropertyType[];
};

export function getFiltersAPI(payload: string): APIService<ParsedDataGFR> {
  const route = `search/property/filter?${payload}`;
  const httpRequest = new HttpRequest<GFRResponse>(getAPIUrl('search', route));

  return {
    send: async () => {
      try {
        const response = await httpRequest.send();
        return parseFilterResponse(response.data); // Parses response to desired format
      } catch (err: any) {
        trackErrorEvents({
          type: 'get_filters_fetch_failed',
          errorStack: err,
        });
        throw err; // Throws error so React Query can handle it
      }
    },
    abort: httpRequest.abort, // Supports abort functionality if needed 
  };
}

function parseFilterResponse(data: ResponseDataGFR): ParsedDataGFR {
  const parseValueToOption = (item: string | number) => ({
    value: String(item),
    label: String(item),
  });
  const parsePropertyTypes = (item: string): PropertyType => {
    const value = item.toLocaleLowerCase();
    const iconType = (
      Object.keys(IconMap).includes(value) ? value : 'default'
    ) as PropertyType['iconType'];
    return {
      //to be removed after BE implementation
      value: item,
      label: item,
      iconType,
    };
  };
  const unwantedValues = ['any', 'all'];
  const filterOutUnwantedValues = (value: string) =>
    !unwantedValues.includes(value.toLowerCase());

  return {
    amenities: data.amenities.map(parseValueToOption),
    area: {min: Math.min(...data.areas), max: Math.max(...data.areas)},
    bathrooms: data.bathrooms.map(parseValueToOption),
    bedrooms: data.bedrooms.map(parseValueToOption),
    completionStatus: data.completionStatus
      .filter(filterOutUnwantedValues)
      .map(parseValueToOption),
    price: {min: Math.min(...data.prices), max: Math.max(...data.prices)},
    types: data.types.map(parsePropertyTypes),
    unitView: data.unitView
      .filter(filterOutUnwantedValues)
      .map(parseValueToOption),
    resaleTypes: data.resaleTypes
      .filter(filterOutUnwantedValues)
      .map(parseValueToOption),
  };
}
